import { action, thunk } from 'easy-peasy';
import * as scriptService from '../../services/script/script';
import {
  FetchScriptsParams,
  Script,
  getDefaultScript,
} from '../../pages/scripts/types';
import { ScriptTestResponse } from '../../services/script/types';

const scriptModel = {
  scriptList: [] as Script[],
  scriptListTotalPages: 0,
  currentScript: null as Script | null,
  runResult: null as ScriptTestResponse | null,
  isRunning: false,
  isDeploying: false,

  setScriptList: action<any, Script[]>((state, payload) => {
    state.scriptList = payload;
  }),

  setScriptListTotalPages: action<any, number>((state, payload) => {
    state.scriptListTotalPages = payload;
  }),

  setCurrentScript: action<any, Script | null>((state, payload) => {
    state.currentScript = payload;
  }),

  setRunResult: action<any, ScriptTestResponse | null>((state, payload) => {
    state.runResult = payload;
  }),

  setIsRunning: action<any, boolean>((state, payload) => {
    state.isRunning = payload;
  }),

  setIsDeploying: action<any, boolean>((state, payload) => {
    state.isDeploying = payload;
  }),

  getScriptList: thunk(
    async (
      actions: any,
      { orgId, params }: { orgId: string; params?: FetchScriptsParams },
    ) => {
      try {
        const response = await scriptService.fetchScripts(orgId, params);
        actions.setScriptList(response?.data?.data ?? []);
        const totalCount = parseInt(
          response?.headers?.['x-total-count'] ?? '0',
          10,
        );
        const pageSize = params?.pageSize ?? 10;
        actions.setScriptListTotalPages(Math.ceil(totalCount / pageSize) || 1);
      } catch (error) {
        console.log('Error fetching scripts', error);
        actions.setScriptList([]);
        actions.setScriptListTotalPages(0);
      }
    },
  ),

  getScriptById: thunk(async (actions: any, { orgId, id }: any) => {
    if (!id) {
      actions.setCurrentScript({ id: '', ...getDefaultScript() });
      return;
    }
    try {
      const response = await scriptService.fetchScriptById(orgId, id);
      actions.setCurrentScript(response?.data?.data ?? null);
    } catch (error) {
      console.log('Error fetching script by id', error);
      actions.setCurrentScript(null);
    }
  }),

  saveScript: thunk(
    async (actions: any, { orgId, data, onSuccess, commonAction }: any) => {
      try {
        const response = data.id
          ? await scriptService.updateScript(orgId, data.id, data)
          : await scriptService.createScript(orgId, data);
        commonAction.setSubmitSnackbar({
          status: true,
          messages: `Script ${data.name} successfully ${
            data.id ? 'updated' : 'created'
          }`,
        });
        if (onSuccess) onSuccess(response?.data?.data);
        return response;
      } catch (error) {
        commonAction.setSubmitSnackbar({
          status: true,
          messages: 'Error saving script',
          error: true,
        });
      }
    },
  ),

  deleteScript: thunk(
    async (actions: any, { orgId, id, params, commonAction }: any) => {
      try {
        await scriptService.deleteScript(orgId, id);
        commonAction.setSubmitSnackbar({
          status: true,
          messages: 'Script successfully deleted',
        });
        actions.getScriptList({ orgId, params });
      } catch (error) {
        commonAction.setSubmitSnackbar({
          status: true,
          messages: 'Error deleting script',
          error: true,
        });
      }
    },
  ),

  testScript: thunk(
    async (
      actions: any,
      { orgId, id, javascriptCode, data, commonAction }: any,
    ) => {
      actions.setIsRunning(true);
      actions.setRunResult(null);
      try {
        const response = await scriptService.testScript(orgId, id, {
          data,
          options: {
            isConsoleOutputEnabled: true,
          },
        });
        actions.setRunResult(response?.data?.data ?? null);
        return response?.data?.data;
      } catch (error) {
        commonAction.setSubmitSnackbar({
          status: true,
          messages: 'Error testing script',
          error: true,
        });
      } finally {
        actions.setIsRunning(false);
      }
    },
  ),

  deployScript: thunk(
    async (actions: any, { orgId, id, commonAction }: any) => {
      actions.setIsDeploying(true);
      try {
        const response = await scriptService.deployScript(orgId, id);
        commonAction.setSubmitSnackbar({
          status: true,
          messages: 'Script deployed successfully',
        });
        return response?.data?.data;
      } catch (error) {
        commonAction.setSubmitSnackbar({
          status: true,
          messages: 'Error deploying script',
          error: true,
        });
      } finally {
        actions.setIsDeploying(false);
      }
    },
  ),
};

export default scriptModel;
